import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ClipboardService } from 'ngx-clipboard';
import { finalize } from 'rxjs/operators';

import { OAuthClient, OAuthScope } from '@app/models';
import { ErrorHandlerService, NotyService, OAuthService } from '@app/services';
import { ImageInputValidationError } from '@app/shared/image-input/image-input.component';
import { SecureUrlDirective } from '@app/shared/validators';

export type ModalResult = OAuthClient;
export interface ModalPublicProperties {
  client?: OAuthClient;
  step?: ModalStep;
}

export enum ModalStep {
  basicInfo,
  authentication
}

@Component({
  selector: 'app-oauth-client-modal',
  templateUrl: './oauth-client-modal.component.html',
  styleUrls: ['./oauth-client-modal.component.scss'],
  standalone: false
})
export class OAuthClientModalComponent implements OnInit {
  readonly ModalStep = ModalStep;
  readonly iconValidation = {
    maxSizeKB: 5120,
    maxHeight: 512, // px
    maxWidth: 512, // px
    minHeight: 256, // px
    minWidth: 256, // px
    mimeTypes: ['image/png']
  };

  form: UntypedFormGroup;
  currentStep = ModalStep.basicInfo;
  step = ModalStep.basicInfo;
  client: OAuthClient;
  availableScopes: OAuthScope[] = [];
  isLoading = false;
  isLoadingScopes = false;
  selectedIconUrl: string;
  private selectedIconFile: File;
  private isCreateFlow = true;

  constructor(
    public modal: NgbActiveModal,
    public translateService: TranslateService,
    private clipboardService: ClipboardService,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private oauthService: OAuthService
  ) {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      icon: [null, Validators.required],
      redirectUris: this.formBuilder.array([]),
      scopes: this.formBuilder.array([], this.hasSelectedScopeValidation)
    });
  }

  ngOnInit() {
    this.isCreateFlow = !this.client;
    this.currentStep = this.step || ModalStep.basicInfo;
    this.fillFormFromClient();
    this.loadScopes();
  }

  submit() {
    if (this.currentStep === ModalStep.basicInfo) {
      this.saveBasicInfo();
      return;
    }

    this.saveAuthentication();
  }

  back() {
    if (this.currentStep === ModalStep.basicInfo) {
      this.modal.dismiss();
      return;
    }

    if (this.isCreateFlow) {
      this.currentStep = ModalStep.basicInfo;
      return;
    }

    this.modal.dismiss();
  }

  selectIcon(file: File) {
    this.selectedIconFile = file;
    this.form.get('icon').setValue(file);
  }

  removeIcon() {
    this.selectedIconFile = null;
    this.selectedIconUrl = null;
    this.form.get('icon').setValue(null);
  }

  showIconValidationError(error: ImageInputValidationError) {
    const errorMap: Record<ImageInputValidationError, string> = {
      dimensions: 'settings.oauthClients.iconDimensionsInvalid',
      maxSize: 'settings.oauthClients.iconSizeInvalid',
      mime: 'settings.oauthClients.iconInvalid'
    };

    this.form.get('icon').markAsUntouched();
    this.notyService.error(
      this.translateService.instant(errorMap[error], {
        ...this.iconValidation,
        maxSize: this.iconValidation.maxSizeKB / 1024
      })
    );
  }

  getActionLabel(): string {
    return this.currentStep === ModalStep.basicInfo && this.isCreateFlow ? 'button.forward' : 'button.save';
  }

  getSecondaryActionLabel(): string {
    return this.currentStep === ModalStep.authentication && this.isCreateFlow ? 'button.back' : 'button.cancel';
  }

  copyClientId() {
    const clientId = this.client?.id;

    if (!clientId) {
      return;
    }

    this.clipboardService.copyFromContent(clientId);
    this.notyService.success(this.translateService.instant('shared.textCopySuccess'));
  }

  private saveBasicInfo() {
    this.form.get('name').markAsTouched();
    this.form.get('description').markAsTouched();
    this.form.get('icon').markAsTouched();

    if (this.form.get('name').invalid || this.form.get('description').invalid || this.form.get('icon').invalid) {
      return;
    }

    if (this.isCreateFlow) {
      this.currentStep = ModalStep.authentication;
      return;
    }

    this.isLoading = true;
    this.oauthService
      .updateClient(this.basicInfoUpdateValue())
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: client => {
          this.client = client;
          this.selectedIconUrl = client.icon_url || this.selectedIconUrl;
          this.notyService.success(this.translateService.instant('settings.oauthClients.updatedWithSuccess'));
          this.modal.close(client);
        },
        error: error => this.handleValidationError(error)
      });
  }

  private saveAuthentication() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const request$ = this.isCreateFlow ? this.oauthService.createClient(this.oauthCreateValues()) : this.oauthService.updateClient(this.oauthAuthenticationValues());

    this.isLoading = true;
    request$.pipe(finalize(() => (this.isLoading = false))).subscribe({
      next: client => {
        this.client = client;
        if (!this.isCreateFlow) {
          this.notyService.success(this.translateService.instant('settings.oauthClients.updatedWithSuccess'));
        }
        this.modal.close(client);
      },
      error: error => this.handleValidationError(error)
    });
  }

  getScopeDescription(scope: OAuthScope): string {
    const translationKey = `settings.oauthApplications.scopePermissions.${scope.id}`;
    const translation = this.translateService.instant(translationKey);

    return translation !== translationKey ? translation : scope.description;
  }

  get redirectUris(): UntypedFormArray {
    return this.form.get('redirectUris') as UntypedFormArray;
  }

  get scopes(): UntypedFormArray {
    return this.form.get('scopes') as UntypedFormArray;
  }

  private loadScopes() {
    this.isLoadingScopes = true;

    this.oauthService
      .scopes()
      .pipe(finalize(() => (this.isLoadingScopes = false)))
      .subscribe({
        next: scopes => {
          this.availableScopes = scopes;
          const selectedScopes = this.isCreateFlow && !this.client?.scopes?.length ? scopes.map(scope => scope.id) : this.client?.scopes || [];

          this.scopes.clear();
          scopes.forEach(scope => this.scopes.push(this.formBuilder.control(selectedScopes.includes(scope.id))));
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  private fillFormFromClient() {
    if (this.client) {
      this.form.patchValue({
        name: this.client.name || '',
        description: this.client.description || '',
        icon: this.client.icon_url || null
      });
      this.selectedIconUrl = this.client.icon_url || null;
    }

    this.redirectUris.clear();
    this.redirectUris.push(this.formBuilder.control((this.client?.redirect_uris || [])[0] || '', [Validators.required, SecureUrlDirective.validator]));
  }

  private oauthCreateValues() {
    return {
      client: {
        name: this.form.get('name').value.trim(),
        description: this.form.get('description').value.trim(),
        icon: this.selectedIconFile,
        redirect_uris: this.redirectValues,
        scopes: this.selectedScopes
      }
    };
  }

  private basicInfoUpdateValue() {
    return {
      id: this.client.id,
      client: {
        name: this.form.get('name').value.trim(),
        description: (this.form.get('description').value || '').trim(),
        ...(this.selectedIconFile ? { icon: this.selectedIconFile } : {})
      }
    };
  }

  private oauthAuthenticationValues() {
    return {
      id: this.client.id,
      client: {
        redirect_uris: this.redirectValues,
        scopes: this.selectedScopes
      }
    };
  }

  private handleValidationError(error: Parameters<ErrorHandlerService['handleValidation']>[1]) {
    const handledError = this.errorHandlerService.handleValidation(this.form, error, {
      transformMap: {
        '^client\\.redirect_uris': 'redirectUris',
        '^client\\.': ''
      }
    });

    this.showFirstStepWithErrors(handledError?.validation);
  }

  private showFirstStepWithErrors(validation: ValidationErrors) {
    if (!validation) {
      return;
    }

    if (this.hasValidationError(validation, ['name', 'description', 'icon'])) {
      this.currentStep = ModalStep.basicInfo;
      return;
    }

    if (this.hasValidationError(validation, ['redirectUris', 'scopes'])) {
      this.currentStep = ModalStep.authentication;
    }
  }

  private hasValidationError(validation: ValidationErrors, fieldNames: string[]): boolean {
    return fieldNames.some(fieldName => !!validation[fieldName]);
  }

  private hasSelectedScopeValidation(control: AbstractControl): ValidationErrors | null {
    return (control as UntypedFormArray).controls.some(scope => !!scope.value) ? null : { required: true };
  }

  private get redirectValues(): string[] {
    const redirectUri = (this.redirectUris.at(0)?.value || '').trim();
    return redirectUri ? [redirectUri] : [];
  }

  private get selectedScopes(): string[] {
    return this.scopes.controls.map((control, index) => (control.value ? this.availableScopes[index]?.id : null)).filter(Boolean);
  }
}
